import { FC, useState, useMemo } from 'react';
import { Button, Col, Form, Input, Row, Select, Space, Table } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import { useApp } from '@/App';
import { useQuery } from '@tanstack/react-query';
import { getNAPList } from '@/libs/fleet-api/services/nap';
import { FleetGetListParams } from '@/libs/fleet-api/fetcher';
import dayjs from 'dayjs';

// Local type definition for NAPList
type NAPList = {
	no: number;
	prospectId: string;
	customer: { id: number; value: string };
	pipelineId: number;
	customerGroup: { id: number; value: string };
	plafonType: { id: number; value: string };
	orderSource: { id: number; value: string };
	status: null;
	dateOfInput: string;
	prospectStep: string;
	reviewStatus: string;
	napStatus: string;
	pipelineStatus: string;
};

const NAPListPage: FC = () => {
	const { navigate } = useApp();

	const [params, setParams] = useState<FleetGetListParams>({
		pagination: {
			pageNumber: 1,
			pageSize: 20,
		},
	});

	const { data, isFetching } = useQuery({
		queryKey: ['getNAPList', params] as const,
		queryFn: ({ queryKey }) => {
			const [, params] = queryKey;
			return getNAPList(params);
		},
	});

	// Type for processed data with rowspan properties
	type ProcessedNAPList = NAPList & {
		key: number;
		originalIndex: number;
		prospectIdRowSpan: number;
		customerRowSpan: number;
		dateOfInputRowSpan: number;
		customerGroupRowSpan: number;
		napStatusRowSpan: number;
		noRowSpan: number;
		inputNAPRowSpan: number;
	};

	// Process data for rowspan functionality
	const processedData = useMemo(() => {
		const rawData = data?.data?.items ?? [];
		if (!rawData.length) return [];

		const groupedByProspectId: Record<
			string,
			(NAPList & { originalIndex: number })[]
		> = {};
		const result: ProcessedNAPList[] = [];

		// Group by prospectId
		rawData.forEach((item, index) => {
			const prospectId = item.prospectId;
			if (!groupedByProspectId[prospectId]) {
				groupedByProspectId[prospectId] = [];
			}
			groupedByProspectId[prospectId].push({
				...item,
				originalIndex: index,
			} as NAPList & { originalIndex: number });
		});

		// Process groups and add rowspan information
		Object.values(groupedByProspectId).forEach((group) => {
			group.forEach((item, groupIndex) => {
				const groupLength = group.length;
				const isFirstInGroup = groupIndex === 0;

				result.push({
					...item,
					key: item.originalIndex,
					// Add rowspan properties for columns that should be merged
					prospectIdRowSpan: isFirstInGroup ? groupLength : 0,
					customerRowSpan: isFirstInGroup ? groupLength : 0,
					dateOfInputRowSpan: isFirstInGroup ? groupLength : 0,
					customerGroupRowSpan: isFirstInGroup ? groupLength : 0,
					napStatusRowSpan: isFirstInGroup ? groupLength : 0,
					noRowSpan: isFirstInGroup ? groupLength : 0,
					inputNAPRowSpan: isFirstInGroup ? groupLength : 0,
				});
			});
		});

		// Sort back to original order
		return result.sort((a, b) => a.originalIndex - b.originalIndex);
	}, [data?.data?.items]);

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<FleetTitle>Nota Analisa Pembiayaan</FleetTitle>

			<Form
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={(params) => {
					setParams({
						pagination: {
							pageNumber: 1,
							pageSize: 20,
						},
						...params,
					});
				}}
			>
				<Form.Item
					noStyle
					shouldUpdate
				>
					{(form) => (
						<Row gutter={[20, 20]}>
							<Col flex="auto">
								<Form.Item
									name={['search', 'by']}
									label={<span>Search By</span>}
									labelCol={{ flex: 'auto' }}
									wrapperCol={{ flex: 'none', style: { width: 250 } }}
									initialValue="customer-name"
								>
									<Select
										placeholder="Search By"
										options={[
											{
												label: 'Customer Name',
												value: 'customer-name',
											},
											{
												label: 'NPWP',
												value: 'npwp',
											},
											{
												label: 'Status',
												value: 'status',
											},
										]}
										style={{
											display: 'block',
											width: '100%',
										}}
										onSelect={() => form.resetFields([['search', 'value']])}
									/>
								</Form.Item>
							</Col>
							<Col style={{ width: 300 }}>
								<Form.Item name={['search', 'value']}>
									{form.getFieldValue(['search', 'by']) === 'status' ? (
										<Select
											options={[
												{ label: 'New', value: '1' },
												{ label: 'Return', value: '2' },
											]}
										/>
									) : (
										<Input
											placeholder="Search..."
											style={{
												display: 'block',
											}}
										/>
									)}
								</Form.Item>
							</Col>
							<Col>
								<Form.Item>
									<Button
										block
										type="primary"
										htmlType="submit"
									>
										SEARCH
									</Button>
								</Form.Item>
							</Col>
						</Row>
					)}
				</Form.Item>
			</Form>

			<Table
				scroll={{ x: 'max-content' }}
				bordered
				loading={isFetching}
				dataSource={processedData}
				columns={[
					{
						title: 'No',
						width: 1,
						align: 'center',
						render: (_, __, index) => index + 1,
						onCell: (record) => ({
							rowSpan: record.noRowSpan,
						}),
					},
					{
						title: 'Prospect ID',
						render: (_, record) => record.prospectId,
						onCell: (record) => ({
							rowSpan: record.prospectIdRowSpan,
						}),
					},
					{
						title: 'Nama Customer',
						render: (_, record) => record.customer?.value,
						onCell: (record) => ({
							rowSpan: record.customerRowSpan,
						}),
					},
					{
						title: 'Date of Input',
						render: (_, record) =>
							record?.dateOfInput &&
							dayjs(record.dateOfInput).format('DD/MM/YYYY'),
						onCell: (record) => ({
							rowSpan: record.dateOfInputRowSpan,
						}),
					},
					{
						title: 'Group BMPP',
						render: (_, record) => record.customerGroup?.value,
						onCell: (record) => ({
							rowSpan: record.customerGroupRowSpan,
						}),
					},
					{
						title: 'Jenis Plafon',
						render: (_, record) => record.plafonType?.value,
					},
					{
						title: 'Sumber Order',
						render: (_, record) => record.orderSource?.value,
					},
					{
						title: 'Status',
						render: (_, record) => record.napStatus,
						onCell: (record) => ({
							rowSpan: record.napStatusRowSpan,
						}),
					},
					{
						fixed: 'right',
						title: 'Input NAP',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									onClick={(e) => {
										e.preventDefault();
										navigate('NAPPage', {
											prospectId: record.prospectId,
										});
									}}
								>
									<ArrowRightOutlined />
								</a>
							);
						},
						onCell: (record) => ({
							rowSpan: record.inputNAPRowSpan,
						}),
					},
				]}
				pagination={{
					current: params.pagination.pageNumber,
					pageSize: 20,
					total: data?.data.totalCount,
					showSizeChanger: false,
					onChange: (pageNumber) => {
						setParams((params) => {
							return {
								...params,
								pagination: {
									...params.pagination,
									pageNumber,
								},
							};
						});
					},
				}}
			/>
		</Space>
	);
};

export default NAPListPage;
